// Security update: the markdown preview is sanitized with sanitizeHtml() before rendering.
/* Blog editor page - Full-featured markdown editor with preview, SEO fields, and scheduling
   Features: Auto-generated slug, markdown preview, SEO optimization, schedule publishing, rich toolbar
   Uses URL param ?id=xxx to load existing blogs
   Updated: Images load from new Supabase project URL
   Updated: Uses blog-images storage bucket for uploads
   Updated: Fixed automatic publishing - scheduled blogs now automatically set published=true and published_at=scheduled_time
   Updated: Redesigned Publicatie section with clear radio button options (Concept / Direct publiceren / Inplannen)
   Updated: Replaced Alert components with toast notifications (bottom-right corner)
   Updated: Added unsaved changes warning - prevents accidental navigation with confirmation dialog
   Updated: Breadcrumb shows hierarchical path: Home / Blogs / [Blog Title] where Blogs is clickable
   Updated: URL slug section now displays full URL (https://buitnplanten.nl/blogs/{slug}) with copy-to-clipboard button */



import { sanitizeHtml } from '@/lib/sanitize-html';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Breadcrumb from '@/components/admin/Breadcrumb';
import MarkdownToolbar from '@/components/admin/MarkdownToolbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Save, ArrowLeft, Eye, Code, Calendar as CalendarIcon, Clock, X, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getBlogImageUrl } from '@/lib/image-url';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tldr?: string;
  image_url?: string;
  image_storage_path?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  og_image?: string;
  og_image_storage_path?: string;
  published: boolean;
  published_at?: string;
  scheduled_at?: string;
  created_at: string;
  updated_at: string;
}

export default function BlogEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get('id');
  const isNew = !blogId || blogId === 'new';

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const [scheduleDate, setScheduleDate] = useState<Date>();
  const [scheduleTime, setScheduleTime] = useState('12:00');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    tldr: '',
    image_url: '',
    image_storage_path: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: '',
    og_image_storage_path: '',
    published: false
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    if (!isNew && blogId) {
      fetchBlog();
    }
  }, [blogId]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (formData.title || formData.content || formData.excerpt) {
      setHasUnsavedChanges(true);
    }
  }, [formData, scheduleDate, scheduleTime]);

  const fetchBlog = async () => {
    if (!blogId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('id', blogId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          tldr: data.tldr || '',
          image_url: data.image_url || '',
          image_storage_path: data.image_storage_path || '',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          meta_keywords: data.meta_keywords || '',
          og_image: data.og_image || '',
          og_image_storage_path: data.og_image_storage_path || '',
          published: data.published
        });

        if (data.image_storage_path) {
          const { data: urlData } = supabase.storage
            .from('blog-images')
            .getPublicUrl(data.image_storage_path);
          setImagePreview(urlData.publicUrl);
        } else if (data.image_url) {
          setImagePreview(data.image_url);
        }

        if (data.scheduled_at) {
          const scheduledDate = new Date(data.scheduled_at);
          setScheduleDate(scheduledDate);
          setScheduleTime(format(scheduledDate, 'HH:mm'));
        }

        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Fout bij het ophalen van blog');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        image_storage_path: filePath,
        image_url: ''
      }));
      setImagePreview(urlData.publicUrl);
      toast.success('Afbeelding succesvol geüpload');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fout bij het uploaden van afbeelding');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (formData.image_storage_path) {
      try {
        await supabase.storage
          .from('blog-images')
          .remove([formData.image_storage_path]);
      } catch (error) {
        console.error('Error removing image:', error);
      }
    }

    setFormData(prev => ({
      ...prev,
      image_storage_path: '',
      image_url: ''
    }));
    setImagePreview('');
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Titel en inhoud zijn verplicht');
      return;
    }

    setIsSaving(true);
    try {
      const slug = formData.slug || generateSlug(formData.title);

      let scheduledAt = null;
      let publishedAt = null;
      let isPublished = formData.published;

      if (scheduleDate && scheduleTime) {
        const [hours, minutes] = scheduleTime.split(':');
        const scheduled = new Date(scheduleDate);
        scheduled.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        scheduledAt = scheduled.toISOString();

        publishedAt = scheduledAt;
        isPublished = true;
      } else if (formData.published) {
        publishedAt = new Date().toISOString();
      }

      const blogData = {
        title: formData.title,
        slug,
        excerpt: formData.excerpt,
        content: formData.content,
        tldr: formData.tldr || null,
        image_url: formData.image_url || null,
        image_storage_path: formData.image_storage_path || null,
        meta_title: formData.meta_title || null,
        meta_description: formData.meta_description || null,
        meta_keywords: formData.meta_keywords || null,
        og_image: formData.og_image || null,
        og_image_storage_path: formData.og_image_storage_path || null,
        published: isPublished,
        published_at: publishedAt,
        scheduled_at: scheduledAt,
      };

      if (isNew) {
        const { data, error } = await supabase
          .from('blogs')
          .insert([blogData])
          .select()
          .single();

        if (error) throw error;

        setHasUnsavedChanges(false);
        toast.success('Blog post succesvol aangemaakt');
        setTimeout(() => navigate(`/kh-admin/blogs/editor?id=${data.id}`), 1500);
      } else {
        const { error } = await supabase
          .from('blogs')
          .update(blogData)
          .eq('id', blogId);

        if (error) throw error;
        setHasUnsavedChanges(false);
        toast.success('Blog post succesvol opgeslagen');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Fout bij het opslaan van blog');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation('/kh-admin/blogs');
      setShowLeaveDialog(true);
    } else {
      navigate('/kh-admin/blogs');
    }
  };

  const handleConfirmLeave = () => {
    setHasUnsavedChanges(false);
    setShowLeaveDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleCancelLeave = () => {
    setShowLeaveDialog(false);
    setPendingNavigation(null);
  };

  const handleScheduleNow = () => {
    const now = new Date();
    setScheduleDate(now);
    setScheduleTime(format(now, 'HH:mm'));
  };

  const handleToolbarInsert = (before: string, after?: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);

    const beforeText = formData.content.substring(0, start);
    const afterText = formData.content.substring(end);

    let newText: string;
    let newCursorPos: number;

    if (after !== undefined) {
      newText = beforeText + before + selectedText + after + afterText;
      newCursorPos = start + before.length + selectedText.length;
    } else {
      newText = beforeText + before + selectedText + afterText;
      newCursorPos = start + before.length + selectedText.length;
    }

    setFormData({ ...formData, content: newText });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const renderMarkdown = (markdown: string) => {
    return markdown
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('### ')) {
          return `<h3 key=${i} class="text-xl font-bold mt-6 mb-3 text-gray-900">${line.substring(4)}</h3>`;
        }
        if (line.startsWith('## ')) {
          return `<h2 key=${i} class="text-2xl font-bold mt-8 mb-4 text-gray-900">${line.substring(3)}</h2>`;
        }
        if (line.startsWith('# ')) {
          return `<h1 key=${i} class="text-3xl font-bold mt-8 mb-4 text-gray-900">${line.substring(2)}</h1>`;
        }
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          return `<li key=${i} class="ml-6 list-disc text-gray-700">${line.trim().substring(2)}</li>`;
        }
        if (line.trim() === '') {
          return `<br key=${i} />`;
        }

        let processed = line
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.+?)\*/g, '<em>$1</em>')
          .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#5a6552] hover:text-[#1e2b22] hover:underline">$1</a>');

        return `<p key=${i} class="mb-4 text-gray-700 leading-relaxed">${processed}</p>`;
      })
      .join('');
  };

  const markdownPreview = useMemo(() => {
    return { __html: sanitizeHtml(renderMarkdown(formData.content)) };
  }, [formData.content]);

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-gray-600">Laden...</div>
        </div>
      </>
    );
  }

  const breadcrumbItems = isNew
    ? [
        { label: 'Blogs', path: '/kh-admin/blogs' },
        { label: 'Nieuwe blog' }
      ]
    : [
        { label: 'Blogs', path: '/kh-admin/blogs' },
        { label: formData.title || 'Blog bewerken' }
      ];

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb items={breadcrumbItems} />

        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={handleBackClick}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terug
            </Button>
            <h1 className="text-4xl font-bold text-jv-primary">
              {isNew ? 'Nieuwe blog' : 'Blog bewerken'}
            </h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basis informatie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Bijvoorbeeld: Tips voor het onderhouden van je haagplanten"
                  />
                </div>

                <div>
                  <Label htmlFor="slug">URL Slug *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="automatisch-gegenereerd"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const fullUrl = `https://buitnplanten.nl/blogs/${formData.slug || 'slug'}`;
                        navigator.clipboard.writeText(fullUrl);
                        toast.success('URL gekopieerd naar klembord');
                      }}
                      title="Kopieer volledige URL"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    URL: https://buitnplanten.nl/blogs/{formData.slug || 'slug'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="excerpt">Samenvatting</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Korte samenvatting van het blog post..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="image">Afbeelding</Label>
                  <div className="space-y-3">
                    {imagePreview ? (
                      <div className="relative inline-block">
                        <img
                          src={imagePreview}
                          alt="Blog preview"
                          className="w-full max-w-md h-48 object-cover rounded-lg border"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={handleRemoveImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <Input
                          id="image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="flex-1"
                        />
                        {uploadingImage && (
                          <span className="text-sm text-gray-500">Uploaden...</span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Upload een afbeelding voor dit blog post
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Inhoud *</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={activeTab === 'write' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('write')}
                    >
                      <Code className="w-4 h-4 mr-2" />
                      Schrijven
                    </Button>
                    <Button
                      size="sm"
                      variant={activeTab === 'preview' ? 'default' : 'outline'}
                      onClick={() => setActiveTab('preview')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
                <CardDescription>
                  Markdown + HTML wordt ondersteund. Je kunt HTML tags gebruiken voor buttons, afbeeldingen, etc.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activeTab === 'write' ? (
                  <div className="space-y-4">
                    <MarkdownToolbar onInsert={handleToolbarInsert} />

                    <Textarea
                      ref={textareaRef}
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Schrijf je blog post hier in Markdown + HTML formaat...\n\nTips:\n- Selecteer tekst en klik op een knop om te formatteren\n- Gebruik de toolbar voor snelle opmaak\n- HTML tags worden ondersteund voor geavanceerde styling"
                      rows={20}
                      className="font-mono"
                    />
                  </div>
                ) : (
                  <div
                    className="prose max-w-none p-4 border rounded-lg min-h-[500px] bg-gray-50"
                    dangerouslySetInnerHTML={markdownPreview}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Publicatie</CardTitle>
                <CardDescription>Wanneer wil je deze blog publiceren?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label
                    className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setFormData({ ...formData, published: false });
                      setScheduleDate(undefined);
                      setScheduleTime('12:00');
                    }}
                  >
                    <input
                      type="radio"
                      name="publish-option"
                      checked={!formData.published && !scheduleDate}
                      onChange={() => {}}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Concept (niet publiceren)</div>
                      <div className="text-xs text-gray-500 mt-0.5">Blog blijft onzichtbaar voor bezoekers</div>
                    </div>
                  </Label>

                  <Label
                    className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setFormData({ ...formData, published: true });
                      setScheduleDate(undefined);
                      setScheduleTime('12:00');
                    }}
                  >
                    <input
                      type="radio"
                      name="publish-option"
                      checked={formData.published && !scheduleDate}
                      onChange={() => {}}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Direct publiceren</div>
                      <div className="text-xs text-gray-500 mt-0.5">Blog wordt meteen zichtbaar na opslaan</div>
                    </div>
                  </Label>

                  <div className="border rounded-lg overflow-hidden">
                    <Label
                      className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        if (!scheduleDate) {
                          setScheduleDate(new Date());
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="publish-option"
                        checked={!!scheduleDate}
                        onChange={() => {}}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Inplannen voor later</div>
                        <div className="text-xs text-gray-500 mt-0.5">Kies datum en tijd voor automatische publicatie</div>
                      </div>
                    </Label>

                    {scheduleDate && (
                      <div className="px-3 pb-3 space-y-3 bg-gray-50">
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">Datum</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal bg-white"
                                size="sm"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(scheduleDate, 'dd MMMM yyyy')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={scheduleDate}
                                onSelect={(date) => {
                                  if (date) {
                                    setScheduleDate(date);
                                    setFormData({ ...formData, published: false });
                                  }
                                }}
                                defaultMonth={new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label htmlFor="time" className="text-xs text-gray-600 mb-1.5 block">Tijd</Label>
                            <Input
                              id="time"
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="bg-white"
                              size={undefined}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleScheduleNow}
                              className="bg-white"
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Nu
                            </Button>
                          </div>
                        </div>

                        {scheduleDate && new Date(scheduleDate.setHours(parseInt(scheduleTime.split(':')[0]), parseInt(scheduleTime.split(':')[1]))) > new Date() && (
                          <div className="text-xs text-[#5a6552] bg-[#e7e1d6]/40 px-2 py-1.5 rounded">
                            Publiceert op {format(scheduleDate, 'dd MMM yyyy')} om {scheduleTime}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SEO</CardTitle>
                <CardDescription>Zoekmachine optimalisatie</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="tldr">TL;DR</Label>
                  <Textarea
                    id="tldr"
                    value={formData.tldr}
                    onChange={(e) => setFormData({ ...formData, tldr: e.target.value })}
                    placeholder="Korte samenvatting voor snippets..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="meta_title">Meta Titel (SEO)</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Optionele aangepaste titel voor zoekmachines"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Laat leeg om de gewone titel te gebruiken
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_description">Meta beschrijving</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Beschrijving voor zoekmachines (150-160 tekens)"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.meta_description.length} / 160 tekens
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_keywords">Keywords</Label>
                  <Input
                    id="meta_keywords"
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    placeholder="haag, tuinieren, onderhoud"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Komma gescheiden
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Niet-opgeslagen wijzigingen</AlertDialogTitle>
            <AlertDialogDescription>
              Je hebt nog niet-opgeslagen wijzigingen. Weet je zeker dat je de pagina wilt verlaten? Alle wijzigingen gaan verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelLeave}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave} className="bg-red-600 hover:bg-red-700">
              Verlaten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
